<template>
	<div
		:class="[$style['editable-field'], {
			[$style['editable-field--disabled']]: disabled
		}]"
	>
		<span
			v-if="showLabel && label"
			:class="$style['editable-field__label']"
		>
			{{ label }}
			<slot name="label-content" />
		</span>
		<div
			:class="[$style['editable-field__field'], {
				[$style['editable-field__field--active']]: isActive,
				[$style['editable-field__field--hidden']]: !showIcon
			}]"
		>
			<div
				v-if="!isActive"
				:class="[$style['editable-field__placeholder'], {
					[$style['editable-field__placeholder--icon-hidden']] : !showIcon,
					[$style['editable-field__placeholder--bold']] : bold,
					[$style['editable-field__placeholder--right']] : orientation == 'right',
					[$style['editable-field__placeholder--small-text']] : smallText,
					[$style['editable-field__placeholder--left']] : orientation == 'left'
				}]"
				@click="!!customAction ? customAction() : enterEditMode()"
			>
				<slot name="display">
					{{ formattedDisplayValue }} {{ icon }}
				</slot>
			</div>
			<div
				v-else
				v-click-outside="saveAndExit"
				:class="$style['editable-field__editor']"
			>
				<slot
					name="editor"
					:value="localValue"
					:input="setLocalValue"
					:save="saveAndExit"
					:exit="exitEditMode"
				>
					<input
						ref="input"
						:value="localValue"
						v-bind="$attrs"
						:class="[$style['editable-field__input'], {
							[$style['editable-field__input--small']] : !!small,
							[$style['editable-field__input--small-text']] : !!smallText
						}]"
						@input="handleInput"
						@keyup.enter="saveAndExit"
						@keyup.esc="exitEditMode"
					/>
				</slot>
			</div>
			<FontAwesomeIcon
				v-if="!isActive"
				:icon="['fal', 'pencil']"
				:class="[$style['editable-field__icon'], {
					[$style['editable-field__icon--hidden']]: !showIcon,
					[$style['editable-field__icon--right']]: orientation == 'left',
					[$style['editable-field__icon--left']]: orientation == 'right'
				}]"
				@click="!!customAction ? customAction() : enterEditMode()"
			/>
			<div
				v-else
				:class="$style['editable-field__grouped-icons']"
			>
				<FontAwesomeIcon
					v-if="!isLoading"
					:icon="['far', 'check']"
					:class="$style['editable-field__icon']"
					@click="saveAndExit"
				/>
				<FontAwesomeIcon
					v-if="!isLoading"
					:icon="['far', 'times']"
					:class="$style['editable-field__icon']"
					@click.prevent="exitEditMode"
				/>
				<FontAwesomeIcon
					v-if="isLoading"
					:icon="['far', 'circle-notch']"
					:class="[$style['editable-field__icon'], $style['editable-field__icon--loader']]"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { getCurrencyFractionDigits, truncateCurrencyValue } from '@/utils/currency'
import { sanitizeDecimal } from '@/utils/functions'

export default {
	name: 'EditableField',
	model: {
		event: 'change'
	},
	props: {
		currency: {
			type: String,
			default: null
		},
		showLabel: {
			type: [String, Boolean],
			default: true
		},
		icon: {
			type: String,
			default: null
		},
		bold: {
			type: Boolean,
			default: false
		},
		showIcon: {
			type: [String, Boolean],
			default: true
		},
		small: {
			type: [Boolean, String],
			default: false
		},
		smallText: {
			type: [Boolean, String],
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		orientation: {
			type: String,
			default: 'left'
		},
		customAction: {
			type: [Function, undefined],
			default: undefined
		},
		disabled: {
			type: Boolean,
			default: false
		},
		label: {
			type: String,
			default: null
		},
		value: {
			type: [String, Number],
			default: null
		},
		save: {
			type: Function,
			default: null
		},
		filter: {
			type: Function,
			default: val => val
		},
		decimals: {
			type: Number,
			default: 2
		}
	},
	data: () => ({
		localValue: null,
		isActive: false,
		isLoading: false
	}),
	computed: {
		currentLocale () {
			return (typeof babel !== 'undefined' && babel.locale) ? babel.locale : 'en-US'
		},
		currencyDecimalPlaces () {
			if (!this.currency) return 2
			return getCurrencyFractionDigits(this.currency, this.currentLocale)
		},
		formattedDisplayValue () {
			// 1. If a custom filter is explicitly provided, prioritize it over auto-formatting
			if (this.$options.propsData && this.$options.propsData.hasOwnProperty('filter')) {
				return this.filter(this.value)
			}

			// 2. If currency is provided (and no custom filter), use smart currency formatting
			if (this.currency && this.value !== null && this.value !== '') {
				let parsedValue = this.value
				if (typeof parsedValue === 'string') {
					parsedValue = parseFloat(parsedValue.replace(',', '.').replace(/\s/g, ''))
				}

				if (!isNaN(parsedValue)) {
					const decimals = this.currencyDecimalPlaces
					// Formats to "1,234.56" or "1234" (JPY, HUF) without the currency symbol
					return new Intl.NumberFormat(this.currentLocale, {
						style: 'decimal',
						minimumFractionDigits: decimals,
						maximumFractionDigits: decimals
					}).format(truncateCurrencyValue(parsedValue, decimals))
				}
			}

			// 3. Fallback to standard filter (likely identity)
			return this.filter(this.value)
		}
	},
	methods: {
		enterEditMode () {
			// UNFORMAT: Strip formatting to raw numbers if currency is active
			if (this.currency && this.value) {
				if (this.currencyDecimalPlaces === 0) {
					const numericValue = parseFloat(String(this.value).replace(/[^0-9.,]/g, '').replace(',', '.'))
					this.localValue = isNaN(numericValue) ? '' : String(truncateCurrencyValue(numericValue, 0))
				} else {
					// Keep digits, dots, commas.
					this.localValue = String(this.value).replace(/[^0-9.,]/g, '')
				}
			} else {
				this.localValue = this.value
			}

			this.isActive = true
			this.$nextTick(() => {
				const input = this.$refs['input'] || this.$el.querySelector('input')
				if (input) input.focus()
			})
		},

		setLocalValue (val) {
			this.localValue = val
		},

		handleInput (e) {
			let val = e.target.value

			// Enforce min attribute (prevent negative values when min >= 0)
			if (this.$attrs.min !== undefined && parseFloat(this.$attrs.min) >= 0) {
				val = val.replace(/-/g, '')
			}

			// VALIDATE
			if (this.currency) {
				const allowedDecimals = this.currencyDecimalPlaces

				if (allowedDecimals === 0) {
					// Integer currencies (JPY, etc) -> digits only, cut at first decimal separator
					const integerOnly = val.replace(/[^0-9.,]/g, '')
					const separatorIndex = integerOnly.search(/[.,]/)
					val = separatorIndex === -1 ? integerOnly : integerOnly.slice(0, separatorIndex)
				} else {
					// Decimal currencies -> digits + 1 separator
					val = sanitizeDecimal(val, allowedDecimals)
				}
			}
			else if (this.$attrs.type === 'integer') {
				val = val.replace(/\D/g, '')
			}
			else if (this.$attrs.type === 'decimal') {
				val = sanitizeDecimal(val, this.decimals)
			}

			this.localValue = val

			// Force update input if characters were stripped
			if (this.$refs.input.value !== val) {
				this.$refs.input.value = val
			}

			// The value being typed, before it is saved
			this.$emit('typing', val)
		},

		async saveAndExit (event) {
			if (!this.isLoading && this.isActive) {
				let finalValue = this.localValue
				if (typeof finalValue === 'string') finalValue = finalValue.trim()

				// Check if value changed
				if (this.value !== finalValue) {
					if (this.save) {
						const tmpValue = this.value
						this.isLoading = true
						try {
							if (this.required && !finalValue) {
								finalValue = this.value
							}
							this.$emit('change', finalValue || null)
							await this.save(finalValue || null)
							this.isLoading = false
							this.exitEditMode(event)
						} catch (error) {
							this.$emit('change', tmpValue || null)
							this.isLoading = false
						}
					} else {
						this.$emit('change', finalValue || null)
						this.exitEditMode(event)
					}
				} else {
					this.exitEditMode(event)
				}
			}
		},
		exitEditMode (_event) {
			this.isActive = false
			this.$emit('exit')
		}
	}
}
</script>

<style lang="scss" module>
.editable-field {
    position: relative;
    display: flex;
    flex-flow: column;

    &--disabled {
        pointer-events: none;
    }

    &__label {
        font-size: 12px;
        color: getColor(caption);
    }

    &__field {
        position: relative;
        display: flex;
        align-items: center;
        margin: 0 (-15px);
        padding: 0 15px;

        &:not(.editable-field__field--active, .editable-field__field--hidden):hover {
            cursor: pointer;
            background-color: getColor(mark);

            .editable-field__icon {
                visibility: visible;

                &--hidden {
                    visibility: hidden !important;
                }
            }
        }

        &--hidden:hover {
            cursor: pointer;

            &::before {
                content: '';
                position: absolute;
                inset: 0;
                background-color: getColor(mark);
                display: block;
                z-index: -99;
            }
        }

        &--active {
            .editable-field__icon {
                visibility: visible;
            }

            &:hover::before {
                content: none;
            }
        }
    }

    &__placeholder {
        display: flex;
        flex: 1;
        min-width: 0;
        padding: 5px 30px 5px 0;
        font-size: 16px;
        overflow: hidden;
        overflow-wrap: break-word;
        white-space: normal;

        &--icon-hidden {
            padding-right: 0;
        }

        &--bold {
            font-weight: bold;
        }

        &--small-text {
            font-size: 14px !important;
        }

        &--left {
            justify-content: flex-start;
        }

        &--right {
            justify-content: flex-end;
            padding: 5px 0 5px 30px;
        }
    }

    &__editor {
        flex: 1;
        min-width: 0;
    }

    &__input {
        width: 100%;
        border: 0;
        border-bottom: 2px solid getColor(caption);
        background-color: getColor(input-background);
        outline: 0;
        font-size: 16px;
        padding: $gutter 0;
        color: getColor(text);
        padding-right: 45px;

        &--small-text {
            font-size: 14px !important;

            @media (hover: none) and (pointer: coarse) {
                font-size: 16px !important;
            }
        }

        &--small {
            max-width: 100px !important;
        }

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            appearance: none;
            margin: 0;
        }

        &[type='number'] {
            border-radius: 0;
            appearance: textfield;
        }
    }


    &__icon {
        position: absolute;
        right: 10px;
        visibility: hidden;
        cursor: pointer;
        font-size: 18px;

        &--left {
            left: 10px;
        }

        &--right {
            right: 10px;
        }

        &--loader {
            animation: rotator 1.4s linear infinite;
        }
    }

    &__grouped-icons {
        position: absolute;
        right: 10px;
        visibility: hidden;
        display: flex;
        margin: 0 (-5px);

        .editable-field__icon {
            position: relative;
            margin: 0 5px;
            color: getColor(caption);

            &:first-child {
                color: getColor(tertiary);
            }
        }
    }
}

@keyframes rotator {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(270deg);
    }
}
</style>
